import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  CheckCircle, Calendar, Clock, Phone, Mail,
  MapPin, Wrench, Users, Award, Star, ArrowRight, MessageCircle
} from 'lucide-react';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function BookingSuccessPage() {
  const { bookingId } = useParams();
  const location = useLocation();
  const [booking, setBooking] = useState(location.state?.booking || null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(!booking);

  useEffect(() => {
    // Trigger confetti animation
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch booking if not in state
        if (!booking && bookingId) {
          const bookingRes = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}`);
          if (bookingRes.ok) {
            setBooking(await bookingRes.json());
          }
        }

        // Fetch company stats
        const statsRes = await fetch(`${BACKEND_URL}/api/company-stats`);
        if (statsRes.ok) {
          setStats(await statsRes.json());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bookingId, booking]);

  const getServiceIcon = (serviceType) => {
    const icons = {
      'installation': '🔧',
      'repair': '🛠️',
      'maintenance': '⚙️',
      'filter-replacement': '💧',
      'amc': '📋'
    };
    return icons[serviceType] || '🔧';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Success Message */}
          <div className="text-center mb-12">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              🎉 Booking Confirmed!
            </h1>
            <p className="text-xl text-gray-600">
              Our expert technician will visit you soon!
            </p>
          </div>

          {/* Booking Details Card */}
          {booking && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-6 border-b">
                <div>
                  <p className="text-sm text-gray-500">Booking ID</p>
                  <p className="text-2xl font-bold text-gray-900">{booking.id}</p>
                </div>
                <div className="text-4xl">
                  {getServiceIcon(booking.serviceType)}
                </div>
              </div>

              {/* Service Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2 text-blue-600 mb-2">
                    <Wrench className="w-5 h-5" />
                    <span className="font-medium">Service Type</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900 capitalize">
                    {booking.serviceType?.replace(/-/g, ' ')}
                  </p>
                  {booking.roModel && (
                    <p className="text-gray-600 mt-1">Model: {booking.roModel}</p>
                  )}
                </div>

                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-2 text-green-600 mb-2">
                    <Calendar className="w-5 h-5" />
                    <span className="font-medium">Scheduled Date</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{booking.preferredDate}</p>
                  <p className="text-gray-600 mt-1">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {booking.preferredTime || 'Flexible timing'}
                  </p>
                </div>
              </div>

              {/* Customer Details */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <MapPin className="w-5 h-5" />
                    <span className="font-medium">Service Address</span>
                  </div>
                  <p className="text-gray-900">{booking.name}</p>
                  <p className="text-gray-600">{booking.address}</p>
                  {booking.city && (
                    <p className="text-gray-600">{booking.city} - {booking.pincode}</p>
                  )}
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <Phone className="w-5 h-5" />
                    <span className="font-medium">Contact Details</span>
                  </div>
                  <p className="text-gray-900">{booking.phone}</p>
                  <p className="text-gray-600">{booking.email}</p>
                </div>
              </div>

              {booking.issue && (
                <div className="mt-6 p-4 bg-yellow-50 rounded-xl">
                  <p className="font-medium text-yellow-800 mb-1">Issue Description:</p>
                  <p className="text-yellow-700">{booking.issue}</p>
                </div>
              )}

              {/* Payment Info */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-2 text-blue-700 font-medium mb-1">
                  <CheckCircle className="w-5 h-5" />
                  Payment: Cash on Delivery
                </div>
                <p className="text-sm text-blue-600">Pay after the service is completed to your satisfaction. No advance payment required.</p>
              </div>

              {/* Track Booking Link */}
              <div className="mt-6 text-center">
                <Link to={`/track/booking/${booking.id}`} className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors" data-testid="track-booking-link">
                  Track Your Booking <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          )}

          {/* Company Stats - Trust Builders */}
          {stats && (
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 text-white mb-8">
              <h3 className="text-xl font-semibold text-center mb-6">
                Our Service Excellence 🏆
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats.years_experience}+</p>
                  <p className="text-sm text-blue-200">Years Experience</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Wrench className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats.service_calls?.toLocaleString()}+</p>
                  <p className="text-sm text-blue-200">Service Calls</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats.team_members}+</p>
                  <p className="text-sm text-blue-200">Expert Technicians</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats.satisfaction_rate}%</p>
                  <p className="text-sm text-blue-200">Satisfaction Rate</p>
                </div>
              </div>
            </div>
          )}

          {/* What to Expect */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">What to Expect</h3>
            <div className="space-y-4">
              {[
                { icon: CheckCircle, title: 'Booking Confirmed', desc: 'Your service request is received', done: true },
                { icon: Phone, title: 'Confirmation Call', desc: 'Our team will call you to confirm timing', done: false },
                { icon: Wrench, title: 'Technician Visit', desc: 'Expert technician will arrive at your location', done: false },
                { icon: Star, title: 'Service Completed', desc: 'Quality service with genuine spare parts', done: false }
              ].map((step, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step.done ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    <step.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${step.done ? 'text-gray-900' : 'text-gray-400'}`}>{step.title}</p>
                    <p className="text-sm text-gray-500">{step.desc}</p>
                  </div>
                  {step.done && <CheckCircle className="w-5 h-5 text-green-600" />}
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/919887068888"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <Link
              to="/"
              className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Back to Home
            </Link>
          </div>

          {/* Support */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Need to modify your booking?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+919887068888"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Phone className="w-5 h-5" />
                +91 98765-43210
              </a>
              <a
                href="mailto:service@rodekho.in"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Mail className="w-5 h-5" />
                service@rodekho.in
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default BookingSuccessPage;
